import { useState, useEffect } from 'react';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Save, FileText } from 'lucide-react';

const DEFAULT_NOTIFICATION_DAYS = [90, 60, 30, 14, 7, 3, 1];
const ROLE_OPTIONS = [
  { id: 'tenant_admin', label: 'Tenant Admin' },
  { id: 'legal', label: 'Legal' },
  { id: 'project_manager', label: 'Project Manager' },
  { id: 'estimator', label: 'Estimator' },
];

export function ContractSettings() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [isSaving, setIsSaving] = useState(false);
  
  const [notificationDays, setNotificationDays] = useState<number[]>([90, 60, 30, 14, 7]);
  const [notifyOnUpload, setNotifyOnUpload] = useState(true);
  const [notifyOnExpiry, setNotifyOnExpiry] = useState(true);
  const [alertRecipients, setAlertRecipients] = useState<string[]>(['tenant_admin', 'legal']);

  useEffect(() => {
    if (settings?.contract_settings) {
      const cs = settings.contract_settings;
      setNotificationDays(cs.notification_days_before_expiry || [90, 60, 30, 14, 7]);
      setNotifyOnUpload(cs.notify_on_contract_upload ?? true);
      setNotifyOnExpiry(cs.notify_on_contract_expiry ?? true);
      setAlertRecipients(cs.expiry_alert_recipients || ['tenant_admin', 'legal']);
    }
  }, [settings]);

  const handleDayToggle = (day: number) => {
    setNotificationDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => b - a)
    );
  };

  const handleRecipientToggle = (role: string) => {
    setAlertRecipients(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSettings({
        ...settings,
        contract_settings: {
          notification_days_before_expiry: notificationDays,
          notify_on_contract_upload: notifyOnUpload,
          notify_on_contract_expiry: notifyOnExpiry,
          expiry_alert_recipients: alertRecipients,
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Contract Expiry Notifications
          </CardTitle>
          <CardDescription>
            Configure when to receive notifications before contracts expire
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label className="text-sm font-medium">Notify me before expiry</Label>
            <div className="flex flex-wrap gap-4">
              {DEFAULT_NOTIFICATION_DAYS.map((day) => (
                <div key={day} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day}`}
                    checked={notificationDays.includes(day)}
                    onCheckedChange={() => handleDayToggle(day)}
                  />
                  <Label htmlFor={`day-${day}`} className="text-sm cursor-pointer">
                    {day} {day === 1 ? 'day' : 'days'}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              You'll receive notifications at each selected interval before a contract expires
            </p>
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notify on contract upload</Label>
                <p className="text-xs text-muted-foreground">
                  Get notified when a new contract is uploaded
                </p>
              </div>
              <Switch
                checked={notifyOnUpload}
                onCheckedChange={setNotifyOnUpload}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notify on contract expiry</Label>
                <p className="text-xs text-muted-foreground">
                  Get notified when a contract expires
                </p>
              </div>
              <Switch
                checked={notifyOnExpiry}
                onCheckedChange={setNotifyOnExpiry}
              />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <Label className="text-sm font-medium">Alert Recipients</Label>
            <p className="text-xs text-muted-foreground">
              Select which roles should receive contract expiry alerts
            </p>
            <div className="flex flex-wrap gap-4">
              {ROLE_OPTIONS.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${role.id}`}
                    checked={alertRecipients.includes(role.id)}
                    onCheckedChange={() => handleRecipientToggle(role.id)}
                  />
                  <Label htmlFor={`role-${role.id}`} className="text-sm cursor-pointer">
                    {role.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
